import React from "react";
import { HolderOutlined } from "@ant-design/icons";

/**
 * Popup container for tooltips on cells of a horizontally scrolling table.
 *
 * rc-trigger keeps a popup inside the visible area of its container's scroll
 * ancestors, so mounting the tooltip in the table's scroll box shifts it back
 * into the table viewport when the anchor cell is partly scrolled under the
 * fixed drag-handle column (with the default body container it opens over the
 * handle column / outside the table). The scroll box is not positioned, so it
 * does not clip the popup and a tooltip can still open above the first row.
 */
export const getTableScrollPopupContainer = (trigger: HTMLElement) =>
  (trigger.closest(".ant-table-body, .ant-table-content") as HTMLElement) ||
  document.body;

/**
 * Returns a column configuration for the drag handle in sortable tables.
 *
 * @param module - The current module name (e.g., "archive-", "review-", etc.)
 * @param isShareholderUser - Boolean indicating if the user is a shareholder
 * @param width - The width of the column (defaults to 40)
 * @param extraCondition - Optional extra condition to show the drag handle for a specific record
 * @param style - Optional CSS properties for the drag handle container
 * @returns An array containing the drag handle column configuration or an empty array if not applicable
 */
export const getDragActionColumn = (
  module: string,
  isShareholderUser: boolean,
  renderHandle: (record: any) => React.ReactNode,
  // FA-176: 24 (table inset) + 22 + 12 (cell padding) puts the first column on
  // the section title's line.
  width: number = 22
) => {
  return !module?.includes("archive-") && !isShareholderUser
    ? [
        {
          title: " ",
          dataIndex: "action",
          key: "action",
          fixed: "left" as const,
          width,
          className: "drag-column-cell !bg-white !z-[20]",
          render: (_: any, record: any) =>
            !record.is_archived ? renderHandle(record) : null},
      ]
    : [];
};

export const getDragActionNormalColumn = (
  module: string,
  isShareholderUser: boolean,
  width: number = 24,
  extraCondition: (record: any) => boolean = () => true,
  style: React.CSSProperties = {}
) => {
  return !module?.includes("archive-") && !isShareholderUser
    ? [
        {
          title: " ",
          dataIndex: "action",
          key: "action",
          fixed: "left" as const,
          width,
          className: "drag-column-cell !bg-white !z-[20]",
          render: (_: any, record: any) => {
            if (record.is_archived) return null;
            
            const conditionResult = extraCondition(record);
            
            // If the caller passed a React component (like <DragHandle />), render it directly
            if (React.isValidElement(conditionResult)) {
                return conditionResult;
            }
            
            // Fallback for when extraCondition returns a boolean
            return (
              <div className="drag-icon">
                {conditionResult && <HolderOutlined />}
              </div>
            );
          },
        },
      ]
    : [];
};
